import { supabase } from '@/lib/supabase';

// =========================================
// Queries (read operations)
// =========================================

/** Fetch all reviews for a team, ordered by creation date desc */
export async function fetchReviewsByTeam(teamId: string) {
  return supabase
    .from('reviews')
    .select('*')
    .eq('team_id', teamId)
    .order('created_at', { ascending: false });
}

/** Fetch a single review by ID */
export async function fetchReviewById(reviewId: string) {
  return supabase
    .from('reviews')
    .select('*')
    .eq('id', reviewId)
    .single();
}

/** Fetch review decisions, optionally filtered by phase */
export async function fetchReviewDecisions(
  reviewId: string,
  phase?: string,
) {
  let query = supabase
    .from('review_decisions')
    .select('*')
    .eq('review_id', reviewId);

  if (phase) {
    query = query.eq('phase', phase);
  }

  return query.order('created_at', { ascending: false });
}

/** Batch fetch profiles by IDs */
export async function fetchProfilesByIds(ids: string[]) {
  return supabase
    .from('profiles')
    .select('id, full_name, email')
    .in('id', ids);
}

/** Fetch review conflicts ordered by resolved status */
export async function fetchReviewConflicts(reviewId: string) {
  return supabase
    .from('review_conflicts')
    .select('*')
    .eq('review_id', reviewId)
    .order('resolved', { ascending: true });
}

/** Batch fetch sources by IDs (id, file_name, title) */
export async function fetchSourcesByIds(ids: string[]) {
  return supabase
    .from('sources')
    .select('id, file_name, title')
    .in('id', ids);
}

/** Fetch review decisions filtered by review and source IDs */
export async function fetchDecisionsByReviewAndSources(
  reviewId: string,
  sourceIds: string[],
) {
  return supabase
    .from('review_decisions')
    .select('*')
    .eq('review_id', reviewId)
    .in('source_id', sourceIds);
}

/** Fetch review mode for a review */
export async function fetchReviewMode(reviewId: string) {
  return supabase
    .from('reviews')
    .select('review_mode')
    .eq('id', reviewId)
    .single();
}

/** Fetch review_sources, optionally filtered by phase */
export function buildReviewSourcesQuery(
  reviewId: string,
  phase?: string,
) {
  const query = supabase
    .from('review_sources')
    .select('source_id, current_phase')
    .eq('review_id', reviewId);

  if (phase) {
    query.eq('current_phase', phase);
  }

  return query;
}

/** Fetch review decisions (source_id, decision, decision_source, phase) */
export function buildDecisionsSummaryQuery(
  reviewId: string,
  phase?: string,
) {
  const query = supabase
    .from('review_decisions')
    .select('source_id, decision, decision_source, phase')
    .eq('review_id', reviewId);

  if (phase) {
    query.eq('phase', phase);
  }

  return query;
}

/** Count conflicts for a review, optionally filtered by phase */
export function buildConflictCountQuery(
  reviewId: string,
  phase?: string,
) {
  let query = supabase
    .from('review_conflicts')
    .select('*', { count: 'exact', head: true })
    .eq('review_id', reviewId);

  if (phase) {
    query = query.eq('phase', phase);
  }

  return query;
}

/** Count resolved conflicts for a review, optionally filtered by phase */
export function buildResolvedConflictCountQuery(
  reviewId: string,
  phase?: string,
) {
  let query = supabase
    .from('review_conflicts')
    .select('*', { count: 'exact', head: true })
    .eq('review_id', reviewId)
    .eq('resolved', true);

  if (phase) {
    query = query.eq('phase', phase);
  }

  return query;
}

/** Fetch human decisions with joined profiles for reviewer stats */
export async function fetchHumanDecisionsWithProfiles(reviewId: string) {
  return supabase
    .from('review_decisions')
    .select('reviewer_id, source_id, decision, decision_source, profiles(full_name, email)')
    .eq('review_id', reviewId)
    .neq('decision_source', 'ai');
}

/** Fetch review_sources with joined source details */
export async function fetchReviewSourcesWithDetails(
  reviewId: string,
  phase?: string,
) {
  let query = supabase
    .from('review_sources')
    .select('*, source:sources(id, file_name, title, authors, abstract, publication_year, journal, doi)')
    .eq('review_id', reviewId);

  if (phase) {
    query = query.eq('current_phase', phase);
  }

  return query.order('screening_priority', { ascending: false });
}

/** Fetch reviewer IDs for kappa computation (non-AI decisions) */
export async function fetchReviewerIdsForKappa(
  reviewId: string,
  phase: string,
) {
  return supabase
    .from('review_decisions')
    .select('reviewer_id, decision_source')
    .eq('review_id', reviewId)
    .eq('phase', phase)
    .neq('decision_source', 'ai');
}

/** Compute Cohen's Kappa via RPC */
export async function rpcComputeCohensKappa(
  reviewId: string,
  reviewer1: string,
  reviewer2: string,
  phase: string,
) {
  return supabase.rpc('compute_cohens_kappa', {
    p_review_id: reviewId,
    p_reviewer1: reviewer1,
    p_reviewer2: reviewer2,
    p_phase: phase,
  });
}

// =========================================
// Mutations (write operations)
// =========================================

/** Insert a new review */
export async function insertReview(params: {
  team_id: string;
  name: string;
  description: string | null;
  blinded: boolean;
  review_mode: string;
  settings: Record<string, unknown>;
}) {
  return supabase
    .from('reviews')
    .insert(params)
    .select()
    .single();
}

/** Update a review by ID */
export async function updateReview(
  reviewId: string,
  updates: Record<string, unknown>,
) {
  return supabase
    .from('reviews')
    .update(updates)
    .eq('id', reviewId)
    .select()
    .single();
}

/** Delete a review by ID */
export async function deleteReview(reviewId: string) {
  return supabase
    .from('reviews')
    .delete()
    .eq('id', reviewId);
}

/** Upsert a review decision */
export async function upsertDecision(params: {
  review_id: string;
  source_id: string;
  reviewer_id: string;
  decision: string;
  reason: string | null;
  phase: string;
  decision_source: string;
  exclusion_reason_category: string | null;
}) {
  return supabase
    .from('review_decisions')
    .upsert(params, {
      onConflict: 'review_id,source_id,reviewer_id,phase',
    })
    .select()
    .single();
}

/** Detect conflicts via RPC */
export async function rpcDetectConflicts(
  reviewId: string,
  phase: string,
) {
  return supabase.rpc('detect_review_conflicts', {
    p_review_id: reviewId,
    p_phase: phase,
  });
}

/** Resolve a conflict (mark as resolved with final decision) */
export async function resolveConflict(
  conflictId: string,
  resolvedBy: string,
  finalDecision: string,
) {
  return supabase
    .from('review_conflicts')
    .update({
      resolved: true,
      resolved_by: resolvedBy,
      final_decision: finalDecision,
      resolved_at: new Date().toISOString(),
    })
    .eq('id', conflictId);
}

/** Upsert sources into a review */
export async function upsertReviewSources(
  rows: Array<{ review_id: string; source_id: string }>,
) {
  return supabase
    .from('review_sources')
    .upsert(rows, { onConflict: 'review_id,source_id' });
}

/** Advance sources to full-text phase via RPC */
export async function rpcAdvanceToFullText(reviewId: string) {
  return supabase.rpc('advance_to_full_text', {
    p_review_id: reviewId,
  });
}
